import { useMemo, useCallback } from "react";
import { EnergySource } from "@/types/energy-source/energy-source.types";
import { FilterOption } from "@/components/ui/table-header/types";
import { EnergySourceWithId } from "./useEnergySourceTable";

interface UseEnergySourceFiltersProps {
    energySources: EnergySourceWithId[];
    searchTerm: string;
    nameFilters: string[];
}

export const useEnergySourceFilters = ({
    energySources,
    searchTerm,
    nameFilters,
}: UseEnergySourceFiltersProps) => {
    const filteredEnergySources = useMemo(() => {
        if (!energySources || energySources.length === 0) return [];

        return energySources.filter((energySource) => {
            // Filter by search term
            if (searchTerm && !energySource.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Filter by name filters
            if (nameFilters.length > 0 && !nameFilters.includes(energySource.name)) {
                return false;
            }

            return true;
        });
    }, [energySources, searchTerm, nameFilters]);

    const getUniqueOptions = useCallback(
        (field: keyof EnergySource): FilterOption[] => {
            if (!energySources || energySources.length === 0) return [];

            const uniqueValues = new Set<string>();

            energySources.forEach((energySource) => {
                if (field === "name" && energySource.name) {
                    uniqueValues.add(energySource.name);
                }
            });

            return Array.from(uniqueValues)
                .sort()
                .map((value) => ({
                    label: value,
                    value: value,
                }));
        },
        [energySources]
    );

    return {
        filteredEnergySources,
        getUniqueOptions,
    };
}; 